import { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import { fetchAllServicios, fetchColumnas, updateColumnas, deleteColumnas } from "./service"
import { showToast } from "utils/swalToast"

// ColumnaConfigurableDTO puede usar clave/etiqueta o key/label
const getColClave = (col) => col.clave ?? col.key ?? col.nombre ?? String(col.id ?? "")
const getColEtiqueta = (col) => col.etiqueta ?? col.label ?? col.nombre ?? getColClave(col)

function AddColumnasVista({ id }) {
  const [servicios, setServicios] = useState([])
  const [selectedServicio, setSelectedServicio] = useState("")
  const [columnas, setColumnas] = useState([]) // ColumnaConfigurableDTO[]

  useEffect(() => {
    fetchAllServicios().then(setServicios)
  }, [])

  const cargarColumnas = (modulo) => {
    fetchColumnas(id, modulo)
      .then((data) => setColumnas(Array.isArray(data) ? data : []))
      .catch(() => setColumnas([]))
  }

  useEffect(() => {
    if (!selectedServicio || !id) return
    const servicio = servicios.find((s) => s.idServicio === selectedServicio)
    cargarColumnas(servicio?.name || selectedServicio)
  }, [selectedServicio, id, servicios])

  const getModulo = () => {
    const servicio = servicios.find((s) => s.idServicio === selectedServicio)
    return servicio?.name || selectedServicio
  }

  // Claves de columnas visibles actualmente
  const visiblesKeys = columnas.filter((c) => c.visible).map(getColClave)

  const handleChangeVisible = (selectedKeys) => {
    setColumnas((prev) =>
      prev.map((col) => ({ ...col, visible: selectedKeys.includes(getColClave(col)) }))
    )
  }

  const handleGuardar = async () => {
    try {
      // PUT espera SucursalColumnaConfigDTO[] (array directo)
      await updateColumnas(id, columnas)
      showToast({ title: "Configuración de columnas guardada", icon: "success" })
    } catch (error) {
      console.error(error)
    }
  }

  const handleRestablecer = async () => {
    try {
      const modulo = getModulo()
      await deleteColumnas(id, modulo)
      showToast({ title: "Configuración restablecida", icon: "success" })
      cargarColumnas(modulo)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <MDBox p={2}>
      <MDTypography variant="h6" mb={2}>
        Configuración de columnas por módulo
      </MDTypography>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={5}>
          <FormControl fullWidth>
            <InputLabel>Servicio</InputLabel>
            <Select
              label="Servicio"
              value={selectedServicio}
              onChange={(e) => setSelectedServicio(e.target.value)}
              sx={{ p: 1.5 }}
            >
              <MenuItem value="">
                <em>Seleccione un servicio</em>
              </MenuItem>
              {servicios.map((s) => (
                <MenuItem key={s.idServicio} value={s.idServicio}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={7}>
          <FormControl fullWidth disabled={!selectedServicio}>
            <InputLabel>Columnas a mostrar</InputLabel>
            <Select
              multiple
              label="Columnas a mostrar"
              value={visiblesKeys}
              onChange={(e) => handleChangeVisible(e.target.value)}
              input={<OutlinedInput label="Columnas a mostrar" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Ninguna"
                  : selected
                      .map((k) => {
                        const col = columnas.find((c) => getColClave(c) === k)
                        return col ? getColEtiqueta(col) : k
                      })
                      .join(", ")
              }
              sx={{ p: 1.5 }}
            >
              {columnas.length === 0 && (
                <MenuItem disabled>
                  <em>
                    {selectedServicio ? "Sin columnas disponibles" : "Seleccione un servicio"}
                  </em>
                </MenuItem>
              )}
              {columnas.map((col) => {
                const clave = getColClave(col)
                return (
                  <MenuItem key={clave} value={clave}>
                    <Checkbox checked={col.visible ?? false} />
                    <ListItemText primary={getColEtiqueta(col)} />
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              variant="outlined"
              color="warning"
              onClick={handleRestablecer}
              disabled={!selectedServicio}
            >
              Restablecer
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleGuardar}
              disabled={!selectedServicio || columnas.length === 0}
            >
              Guardar
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  )
}

export default AddColumnasVista
